import React, { FunctionComponent, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import Card from "components/atoms/card/Card";
import { useAuthUser } from "hooks/useAuthUser";
import { paletteTheme } from "styles/themes/palette";
import { PerimeterCount } from "interfaces/perimeter/perimeter";
import { KeyNumbers } from "interfaces/transaction/transaction";
import { useEffectAbortable } from "hooks/useEffectAbortable";
import { perimeterService } from "services/user/PerimeterService";
import { errorUtils } from "utils/api/errorUtils";
import { PortfolioKeyNumbersDisplay } from "components/molecules/portfolio/PortfolioKeyNumbersDisplay";
import { DistributorModeToggle } from "components/molecules/portfolio/DistributorModeToggle";
import { PortfolioTopSection } from "components/molecules/portfolio/PortfolioTopSection";
import { PortfolioTrendsSection } from "components/molecules/portfolio/PortfolioTrendsSection";

export const PortfolioView: FunctionComponent = () => {
  const { user, userDetails } = useAuthUser();
  const [distributionMode, setDistributionMode] = useState<boolean>(user.distributor);
  const [toggleCompanyMode, setToggleCompanyMode] = useState<boolean>(true);

  const handleToggleCompany = (value: boolean) => {
    setToggleCompanyMode(value);
  };

  const [nbEntities, setNbEntities] = React.useState<PerimeterCount>({
    nbMerchants: 0,
    nbCompanies: 0,
  });

  const [aggregatedKeyNumbers, setAggregatedKeyNumbers] = React.useState<KeyNumbers>({
    totalCommission: 0,
    totalDistributorComission: 0,
  });

  useEffectAbortable((ac) => {
    perimeterService
      .count({ signal: ac.signal })
      .then((response) => {
        setNbEntities(response);
      })
      .catch(errorUtils.handleBackErrors);
  }, []);

  const onChangeDistributionMode = (checked: boolean) => {
    setDistributionMode(checked);
  };

  const changeAggregatedKeyNumber = (keyValues: KeyNumbers):void => {
    setAggregatedKeyNumbers(prev => ({...prev, ...keyValues}))
  }

  

  // // Calculate keyNumbers only when entities, aggregations or toggle change
  const keyNumbers: KeyNumbers = useMemo(
    () => ({
      nbEntities: toggleCompanyMode ? nbEntities.nbCompanies : nbEntities.nbMerchants,
      ...aggregatedKeyNumbers,
    }),
    [nbEntities, aggregatedKeyNumbers, toggleCompanyMode]
  );

  return (
    <>
    <Card
      cardTitle={
        <>
          <Typography variant="h1" color={paletteTheme.palette.primary["main"]}>
            <FormattedMessage id="my_portfolio" />
          </Typography>
          <DistributorModeToggle
            user={user}
            onChangeDistributionMode={onChangeDistributionMode}
            distributionMode={distributionMode}
          />
        </>
      }>
      <PortfolioKeyNumbersDisplay
        distributorMode={distributionMode}
        keyNumbers={keyNumbers}
        isCompany={toggleCompanyMode}
      />

      <PortfolioTopSection
        userDetails={userDetails}
        isDistributor={distributionMode}
        onChangeCompanyMode={handleToggleCompany}
        setKeyNumbers={changeAggregatedKeyNumber}
      />


    </Card>
    <Card 
    cardTitle={
      <>
            <Box marginY={3}>
        <Typography variant="h1" color={paletteTheme.palette.primary["main"]}>
          <FormattedMessage id="title_volume_trends" />
        </Typography>
      </Box>

      </>
    }>
      <PortfolioTrendsSection userDetails={userDetails}/>
    </Card>
    </>
  );
};
